import StarRoundedIcon from "@mui/icons-material/StarRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import { getLandingSegments } from "../../landingEditor/landingLayout";
import { normalizeCalendlyUrl } from "../../../../utils/calendly";
import { DEFAULT_SEGMENT_VISUAL_STYLE } from "../segmentVisualStyle";
import { getSegmentDefinition, SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { landingCopyConstraint } from "../copyLimits";

const TAB_INDEX = { content: 0, colors: 1, style: 1, type: 1, image: 0, behavior: 2 };
const INDEX_TAB = ["content", "design", "behavior"];
const CONTENT_SECTION_BY_TOOL = Object.freeze({
  image: "image-content",
});
const DESIGN_SECTION_BY_TOOL = Object.freeze({
  colors: "colors",
  style: "advanced",
  type: "type",
});

const FONT_PRESETS = [
  {
    id: "friendly",
    label: "Cercana",
    heading: "Exo 2",
    body: "Asap",
    url: "https://fonts.googleapis.com/css2?family=Asap:wght@400;500;600;700&family=Exo+2:wght@600;700;800;900&display=swap",
  },
  {
    id: "modern",
    label: "Moderna",
    heading: "Poppins",
    body: "Inter",
    url: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@600;700;800;900&display=swap",
  },
  {
    id: "strong",
    label: "Comercial",
    heading: "Montserrat",
    body: "Open Sans",
    url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800;900&family=Open+Sans:wght@400;500;600;700&display=swap",
  },
  {
    id: "elegant",
    label: "Elegante",
    heading: "Playfair Display",
    body: "Lato",
    url: "https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&family=Playfair+Display:wght@600;700;800;900&display=swap",
  },
];

const COLOR_DEFAULTS = {
  primaryColor: "#F35A3A",
  secondaryColor: "#19242F",
  surfaceColor: "#FFFFFF",
  backgroundColor: "#EEF0F5",
  textColor: "#1A1A2E",
  mutedTextColor: "#5B5D73",
};

const PALETTE_LABELS = [
  "Principal",
  "Secundario",
  "Superficie",
  "Fondo general",
  "Texto principal",
];

const PALETTE_THEME_PATHS = [
  "theme.primaryColor",
  "theme.secondaryColor",
  "theme.surfaceColor",
  "theme.backgroundColor",
  "theme.textColor",
];

const THEME_COLOR_TOKEN_PATHS = Object.freeze({
  "theme.primaryColor": "designTokens.colors.primary",
  "theme.secondaryColor": "designTokens.colors.secondary",
  "theme.surfaceColor": "designTokens.colors.surface",
  "theme.backgroundColor": "designTokens.colors.background",
  "theme.textColor": "designTokens.colors.text",
  "theme.mutedTextColor": "designTokens.colors.mutedText",
  "theme.dividerColor": "designTokens.colors.divider",
});

const SECTION_COLOR_TOKENS = Object.freeze([
  ["primary", "Principal"],
  ["secondary", "Secundario"],
  ["surface", "Superficie"],
  ["background", "Fondo"],
  ["text", "Texto principal"],
  ["mutedText", "Texto secundario"],
  ["divider", "Bordes / divisores"],
  ["onPrimary", "Texto sobre principal"],
  ["onSecondary", "Texto sobre secundario"],
]);

function normalizeEditableColor(value) {
  const candidate = String(value ?? "").trim();
  if (!candidate) return "";
  const lowered = candidate.toLowerCase();
  if (["transparent", "transparente", "trasparente"].includes(lowered)) return "transparent";
  return candidate;
}

function isTransparentColor(value) {
  return normalizeEditableColor(value) === "transparent";
}

function globalSectionPalette(form) {
  const tokens = form?.designTokens?.colors || {};
  return {
    primary: tokens.primary || form?.theme?.primaryColor || "#F35A3A",
    secondary: tokens.secondary || form?.theme?.secondaryColor || "#19242F",
    surface: tokens.surface || form?.theme?.surfaceColor || "#FFFFFF",
    background: tokens.background || form?.theme?.backgroundColor || "#EEF0F5",
    text: tokens.text || form?.theme?.textColor || "#1A1A2E",
    mutedText: tokens.mutedText || form?.theme?.mutedTextColor || "#5B5D73",
    divider: tokens.divider || form?.theme?.dividerColor || "#E1E4EA",
    onPrimary: tokens.onPrimary || "#FFFFFF",
    onSecondary: tokens.onSecondary || "#FFFFFF",
  };
}

function paletteFromForm(form) {
  return [
    form?.theme?.primaryColor || COLOR_DEFAULTS.primaryColor,
    form?.theme?.secondaryColor || COLOR_DEFAULTS.secondaryColor,
    form?.theme?.surfaceColor || COLOR_DEFAULTS.surfaceColor,
    form?.theme?.backgroundColor || COLOR_DEFAULTS.backgroundColor,
    form?.theme?.textColor || COLOR_DEFAULTS.textColor,
  ].map(normalizeEditableColor);
}

function siteColorChanges(path, value) {
  const normalized = normalizeEditableColor(value);
  const changes = { [path]: normalized };
  const tokenPath = THEME_COLOR_TOKEN_PATHS[path];
  if (tokenPath) changes[tokenPath] = normalized;
  return changes;
}

function paletteChanges(palette) {
  return Object.assign(
    {},
    ...PALETTE_THEME_PATHS.map((path, index) =>
      siteColorChanges(path, normalizeEditableColor(palette[index]))
    )
  );
}

function hexRgb(color) {
  const value = toColorInput(color).slice(1);
  return [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
}

function relativeLuminance(color) {
  const channels = hexRgb(color).map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrastRatio(first, second) {
  const light = Math.max(relativeLuminance(first), relativeLuminance(second));
  const dark = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (light + 0.05) / (dark + 0.05);
}

function palettePermutations(values) {
  if (values.length <= 1) return [values];
  return values.flatMap((value, index) =>
    palettePermutations(values.filter((_, current) => current !== index)).map((rest) => [value, ...rest])
  );
}

function randomizedReadablePalette(palette) {
  const values = palette.map(toColorInput);
  const readable = palettePermutations(values).filter((candidate) =>
    contrastRatio(candidate[3], candidate[4]) >= 3 &&
    contrastRatio(candidate[2], candidate[4]) >= 3
  );
  const options = readable.length ? readable : palettePermutations(values);
  if (options.length <= 1) return values;

  const currentKey = values.join("|");
  const alternatives = options.filter((candidate) => candidate.join("|") !== currentKey);
  const candidates = alternatives.length ? alternatives : options;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

const EMPTY_SEGMENT_PROPERTIES = Object.freeze([]);

function getAtPath(source, path, fallback = "") {
  const value = String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, rawKey) => {
      const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
      return current?.[key];
    }, source);
  return value === undefined || value === null ? fallback : value;
}

const CUSTOM_SECTION_LAYOUT_OPTIONS = Object.freeze([
  { value: "image-right", label: "Título y contenido a la izquierda" },
  { value: "image-left", label: "Título y contenido a la derecha" },
  { value: "centered", label: "Título y contenido centrados" },
]);

const CUSTOM_SECTION_IMAGE_POSITION_OPTIONS = Object.freeze([
  { value: "center", label: "Centro" },
  { value: "top", label: "Arriba" },
  { value: "bottom", label: "Abajo" },
  { value: "left", label: "Izquierda" },
  { value: "right", label: "Derecha" },
]);

function editableLayoutSegment(form, sectionId) {
  const segments = Array.isArray(form?.layout?.segments) ? form.layout.segments : [];
  const index = segments.findIndex((segment) => segment?.id === sectionId);
  return index >= 0 ? { segment: segments[index], index } : { segment: null, index: -1 };
}

function withLandingCopyConstraint(field) {
  const constraint = landingCopyConstraint(field?.path);
  return constraint ? { ...field, copyRole: constraint.role, maxLength: constraint.max } : field;
}

function customSectionDefinition(segment, layoutIndex) {
  if (!segment || segment.type !== "custom" || layoutIndex < 0) return null;
  const root = `layout.segments.${layoutIndex}`;
  const customRoot = `${root}.data.custom`;
  const title = String(segment?.data?.custom?.title || "").trim();
  const anchor = String(segment?.anchor || "").trim();
  const visualDefault = { ...DEFAULT_SEGMENT_VISUAL_STYLE, ...(segment.visualStyle || {}) };
  const label = title
    ? `Sección libre · ${title}`
    : anchor
      ? `Sección libre · ${anchor}`
      : "Sección libre";

  return {
    id: segment.id,
    type: "custom",
    label,
    visibilityPath: `${root}.visible`,
    visibilityLabel: "Mostrar sección libre",
    defaultVisible: segment.visible !== false,
    requiresProducts: false,
    elementEditingReady: true,
    shellPropertyPaths: [`${customRoot}.layout`],
    elementTargets: {
      "custom.eyebrow": { kind: "text", label: "Texto superior", fieldPath: `${customRoot}.eyebrow` },
      "custom.title": { kind: "text", label: "Título", fieldPath: `${customRoot}.title` },
      "custom.description": { kind: "text", label: "Descripción", fieldPath: `${customRoot}.description` },
      "custom.ctaLabel": {
        kind: "button",
        label: "Botón",
        labelPath: `${customRoot}.ctaLabel`,
        urlPath: `${customRoot}.ctaUrl`,
        chips: ["Link"],
      },
      "@custom.image": { kind: "image", label: "Imagen", fieldPath: `${customRoot}.imageUrl` },
      "@custom.video": { kind: "custom-embed", label: "Video", editorMode: "video", fieldPath: `${customRoot}.youtubeUrl` },
      "@custom.reservation": { kind: "custom-embed", label: "Reserva", editorMode: "reservation", fieldPath: `${customRoot}.calendlyUrl` },
    },
    properties: [
      { path: `${root}.visible`, type: SEGMENT_FIELD_TYPES.BOOLEAN, label: "Mostrar esta sección", defaultValue: true },
      { path: `${customRoot}.eyebrow`, type: SEGMENT_FIELD_TYPES.TEXT, label: "Texto superior (opcional)", maxLength: 180, defaultValue: "" },
      { path: `${customRoot}.title`, type: SEGMENT_FIELD_TYPES.TEXTAREA, label: "Título", maxLength: 220, defaultValue: "Nueva sección" },
      { path: `${customRoot}.description`, type: SEGMENT_FIELD_TYPES.TEXTAREA, label: "Contenido", maxLength: 3200, defaultValue: "" },
      { path: `${customRoot}.youtubeUrl`, type: SEGMENT_FIELD_TYPES.TEXT, label: "Video de YouTube (opcional)", maxLength: 2000, defaultValue: "" },
      {
        path: `${customRoot}.calendlyUrl`,
        type: SEGMENT_FIELD_TYPES.URL,
        label: "Calendly (opcional)",
        maxLength: 2000,
        defaultValue: "",
        validator: "calendly",
        helperText: "Pegá el enlace HTTPS de un evento de Calendly. Se renderiza el calendario dentro de esta sección.",
      },
      { path: `${customRoot}.ctaLabel`, type: SEGMENT_FIELD_TYPES.TEXT, label: "Texto del botón (opcional)", maxLength: 100, defaultValue: "" },
      { path: `${customRoot}.ctaUrl`, type: SEGMENT_FIELD_TYPES.URL, label: "Destino del botón", maxLength: 2000, defaultValue: "", destinationPicker: true },
      {
        path: `${customRoot}.imageUrl`,
        type: SEGMENT_FIELD_TYPES.IMAGE,
        label: "Imagen de la sección (opcional)",
        maxLength: 2000,
        defaultValue: "",
        altPath: `${customRoot}.imageAlt`,
        positionPath: `${customRoot}.imagePosition`,
        positionLabel: "Encuadre de la imagen",
        positionOptions: CUSTOM_SECTION_IMAGE_POSITION_OPTIONS,
        positionDefaultValue: "center",
      },
      { path: `${customRoot}.imageAlt`, type: SEGMENT_FIELD_TYPES.TEXT, label: "Texto alternativo de la imagen", maxLength: 220, defaultValue: "" },
      { path: `${customRoot}.imagePosition`, type: SEGMENT_FIELD_TYPES.SELECT, label: "Encuadre de la imagen", options: CUSTOM_SECTION_IMAGE_POSITION_OPTIONS, defaultValue: "center" },
      { path: `${customRoot}.layout`, type: SEGMENT_FIELD_TYPES.SELECT, label: "Distribución", options: CUSTOM_SECTION_LAYOUT_OPTIONS, defaultValue: "image-right" },
      { path: "theme.primaryColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Color principal", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#F35A3A" },
      { path: "theme.secondaryColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Color secundario", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#19242F" },
      { path: "theme.surfaceColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Superficie", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#FFFFFF" },
      { path: "theme.backgroundColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Fondo general", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#EEF0F5" },
      { path: "theme.textColor", type: SEGMENT_FIELD_TYPES.COLOR, label: "Texto principal", maxLength: 100, colorScope: "site", shared: true, defaultValue: "#1A1A2E" },
      {
        path: `${root}.visualStyle`,
        type: SEGMENT_FIELD_TYPES.SEGMENT_VISUAL_STYLE,
        key: "visualStyle",
        label: "Estilo visual del segmento",
        sectionId: "custom",
        defaultValue: visualDefault,
        limits: {
          titleSize: { min: 1, max: 3, step: 1 },
          imageSize: { min: 1, max: 3, step: 1 },
          spacing: { min: 1, max: 3, step: 1 },
          textStyle: { min: 1, max: 3, step: 1 },
        },
      },
    ].map(withLandingCopyConstraint),
  };
}

function withSectionBackgroundProperties(definition, segment, layoutIndex) {
  if (!definition || !segment || layoutIndex < 0) return definition;
  // Spotlight ya tiene un fondo nativo real (catalog.spotlight.backgroundImageUrl).
  // No duplicamos el mismo control con otro almacenamiento.
  if (segment.type === "spotlight") return definition;
  const root = `layout.segments.${layoutIndex}.visualStyle`;
  const backgroundProperties = [
    {
      path: `${root}.backgroundImageUrl`,
      type: SEGMENT_FIELD_TYPES.IMAGE,
      label: "Imagen de fondo de la sección",
      maxLength: 2000,
      defaultValue: "",
      positionPath: `${root}.backgroundImagePosition`,
    },
    {
      path: `${root}.backgroundImagePosition`,
      type: SEGMENT_FIELD_TYPES.SELECT,
      label: "Posición de la imagen de fondo",
      options: CUSTOM_SECTION_IMAGE_POSITION_OPTIONS,
      defaultValue: "center",
    },
  ];

  const existing = new Set((definition.properties || []).map((property) => property.path));
  return {
    ...definition,
    properties: [
      ...(definition.properties || []),
      ...backgroundProperties.filter((property) => !existing.has(property.path)),
    ],
  };
}

const CONTENT_FIELD_TYPES = new Set([
  SEGMENT_FIELD_TYPES.TEXT,
  SEGMENT_FIELD_TYPES.TEXTAREA,
  SEGMENT_FIELD_TYPES.URL,
  SEGMENT_FIELD_TYPES.EMAIL,
  SEGMENT_FIELD_TYPES.IMAGE,
]);

const BEHAVIOR_FIELD_TYPES = new Set([
  SEGMENT_FIELD_TYPES.URL,
  SEGMENT_FIELD_TYPES.BOOLEAN,
]);

const DIRECT_CONTENT_FIELD_TYPES = new Set([
  SEGMENT_FIELD_TYPES.TEXT,
  SEGMENT_FIELD_TYPES.TEXTAREA,
  SEGMENT_FIELD_TYPES.EMAIL,
]);

const COLLECTION_CONTENT_FIELD_TYPES = new Set([
  SEGMENT_FIELD_TYPES.TEXT,
  SEGMENT_FIELD_TYPES.TEXTAREA,
  SEGMENT_FIELD_TYPES.EMAIL,
  SEGMENT_FIELD_TYPES.URL,
]);

const HIGHLIGHT_ICON_COMPONENTS = Object.freeze({
  star: StarRoundedIcon,
  support: SupportAgentRoundedIcon,
  verified: VerifiedRoundedIcon,
  speed: BoltRoundedIcon,
  quality: WorkspacePremiumRoundedIcon,
  local: StorefrontRoundedIcon,
  security: ShieldRoundedIcon,
  autoawesome: AutoAwesomeRoundedIcon,
  event: EventAvailableRoundedIcon,
  groups: GroupsRoundedIcon,
  photo: PhotoCameraRoundedIcon,
  design: DesignServicesRoundedIcon,
});

const COLLECTION_ITEM_LABELS = Object.freeze({
  alt: "Texto alternativo",
  description: "Descripción",
  imageAlt: "Texto alternativo",
  name: "Nombre",
  query: "Búsqueda asociada",
  subtitle: "Descripción",
  title: "Título",
});

function collectionTextFields(property) {
  if (!property?.item) return [];
  if (COLLECTION_CONTENT_FIELD_TYPES.has(property.item.type)) {
    return [{ key: "", label: property.label || "Texto", type: property.item.type, maxLength: property.item.maxLength }];
  }
  if (!Array.isArray(property.item.fields)) return [];
  return property.item.fields
    .filter((field) =>
      COLLECTION_CONTENT_FIELD_TYPES.has(field.type) ||
      (field.type === SEGMENT_FIELD_TYPES.SELECT && field.key === "icon")
    )
    .map((field) => ({
      ...field,
      label: field.label || COLLECTION_ITEM_LABELS[field.key] || "Texto",
    }));
}


const NON_VISUAL_TEXT_PATH = /(?:ariaLabel|imageAlt|logoAlt|\.alt$|whatsappMessage|mapTitle)$/i;
const NON_VISUAL_TEXT_EXACT_PATHS = new Set([
  "business.whatsapp",
  "business.city",
  "business.province",
  "business.country",
  // Estos textos no tienen un nodo visual propio: se usan como fallback/aria-label.
  "catalog.featured.ctaLabel",
  "catalog.brands.ctaLabel",
]);

function elementTextColorEligible(path, field = {}) {
  const candidate = String(path || "").trim();
  if (!candidate) return false;
  if (NON_VISUAL_TEXT_PATH.test(candidate)) return false;
  if (NON_VISUAL_TEXT_EXACT_PATHS.has(candidate)) return false;
  if (/^business\.socials\./i.test(candidate)) return false;
  if (String(field?.key || "").toLowerCase() === "id") return false;
  if (["alt", "imageAlt", "query"].includes(String(field?.key || ""))) return false;
  return true;
}

function elementTextFallback(path, palette, sectionId) {
  const candidate = String(path || "").toLowerCase();
  const colors = palette || {};
  const inverse = ["hero", "spotlight"].includes(String(sectionId || "").toLowerCase());
  if (candidate.includes("navigation.buttons.")) {
    return colors.secondary || colors.text || "#000000";
  }
  if (candidate.includes("secondaryctalabel") || candidate.includes("detailsctalabel")) {
    return colors.secondary || colors.text || "#000000";
  }
  if (candidate.includes("ctalabel") || candidate.endsWith("buttonlabel")) {
    return colors.onPrimary || "#FFFFFF";
  }
  if (inverse) return colors.onSecondary || colors.text || "#FFFFFF";
  if (candidate.includes("eyebrow") || candidate.includes("typecolor")) {
    return colors.primary || colors.text || "#000000";
  }
  if (candidate.includes("title") || candidate.endsWith(".name")) {
    return colors.secondary || colors.text || "#000000";
  }
  if (
    candidate.includes("subtitle") ||
    candidate.includes("description") ||
    candidate.includes("loadingtext") ||
    candidate.includes("emptytext") ||
    candidate.includes("errortext")
  ) {
    return colors.mutedText || colors.text || "#000000";
  }
  return colors.text || "#000000";
}

function registeredFieldValue(form, field, fallback = "") {
  const registeredFallback = field?.fallbackPath
    ? getAtPath(form, field.fallbackPath, fallback)
    : field?.type === SEGMENT_FIELD_TYPES.COLLECTION
      ? []
      : CONTENT_FIELD_TYPES.has(field?.type)
        ? fallback
        : field?.defaultValue ?? fallback;
  return getAtPath(form, field?.path, registeredFallback);
}

function inheritedColorValue(form, field) {
  if (field?.fallbackPath) {
    return getAtPath(form, field.fallbackPath, field.fallbackValue || field.defaultValue || "");
  }
  if (field?.fallbackValue !== undefined) return field.fallbackValue;
  return field?.defaultValue || "";
}

function sectionColorSeed(form, field) {
  if (field?.overrideSeedPath) {
    return getAtPath(form, field.overrideSeedPath, inheritedColorValue(form, field));
  }
  if (field?.overrideSeedValue !== undefined) return field.overrideSeedValue;
  return inheritedColorValue(form, field);
}

function isSupportedColor(value) {
  const candidate = normalizeEditableColor(value);
  if (!candidate) return true;
  if (typeof CSS !== "undefined" && typeof CSS.supports === "function") {
    return CSS.supports("color", candidate);
  }
  return /^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|var\(--[^)]+\)|[a-z]+)$/i.test(candidate);
}

function persistedCssColorError(value, maxLength = 100) {
  const candidate = normalizeEditableColor(value);
  if (!candidate) return "";
  if (Number.isFinite(Number(maxLength)) && candidate.length > Number(maxLength)) {
    return `Máximo ${Number(maxLength)} caracteres.`;
  }
  const valid =
    candidate.toLowerCase() === "transparent" ||
    /^#[0-9a-fA-F]{3,8}$/.test(candidate) ||
    /^rgba?\(\s*[0-9.]+%?\s*,\s*[0-9.]+%?\s*,\s*[0-9.]+%?(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/.test(candidate) ||
    /^hsla?\(\s*[0-9.]+(?:deg)?\s*,\s*[0-9.]+%\s*,\s*[0-9.]+%(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/.test(candidate);
  return valid ? "" : "Usá HEX, rgb/rgba, hsl/hsla o transparent.";
}

function registryFieldError(field, value) {
  if (!field) return "";
  const text = String(value ?? "");
  if (Number.isFinite(field.maxLength) && text.length > field.maxLength) {
    return `Máximo ${field.maxLength} caracteres.`;
  }
  if (field.type === SEGMENT_FIELD_TYPES.COLOR && !isSupportedColor(text)) {
    return "Ingresá un color CSS válido, por ejemplo #F35A3A.";
  }
  if (
    field.type === SEGMENT_FIELD_TYPES.SELECT &&
    Array.isArray(field.options) &&
    field.options.length &&
    !field.options.some((option) => option.value === value)
  ) {
    return "Elegí una opción válida.";
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER && text !== "") {
    const number = Number(value);
    const min = Number.isFinite(field.min) ? field.min : field.limits?.min;
    const max = Number.isFinite(field.max) ? field.max : field.limits?.max;
    if (!Number.isFinite(number)) return "Ingresá un número válido.";
    if (Number.isFinite(min) && number < min) return `El mínimo es ${min}.`;
    if (Number.isFinite(max) && number > max) return `El máximo es ${max}.`;
  }
  if (field.validator === "calendly" && text.trim() && !normalizeCalendlyUrl(text)) {
    return "Usá un enlace HTTPS de un evento válido de Calendly (calendly.com/usuario/evento).";
  }
  return "";
}


function isSectionAnchorTarget(value) {
  const target = String(value || "").trim();
  return target.startsWith("#") && !target.startsWith("#/");
}

function manualDestinationLinkError(field, value) {
  const target = String(value || "").trim();
  const baseError = registryFieldError(field, target);
  if (baseError) return baseError;
  if (!target) return "";
  if (isSectionAnchorTarget(target)) {
    return "Para ir a una sección de la landing, elegila desde el selector. No escribas el ancla a mano.";
  }
  if (!/^(https?:\/\/|\/|#\/|mailto:|tel:)/i.test(target)) {
    return "Ingresá un link válido: https://…, /ruta, mailto:… o tel:….";
  }
  return "";
}

function segmentAvailableAsDestination(form, segment, productsEnabled) {
  if (!segment || ["navbar", "footer"].includes(segment.type)) return false;
  if (segment.type === "custom") return segment.visible !== false;
  const definition = getSegmentDefinition(segment.type);
  if (!definition) return false;
  if (definition.requiresProducts && (!productsEnabled || getAtPath(form, "sections.showProducts", true) === false)) return false;
  if (segment.type === "spotlight" && getAtPath(form, "catalog.spotlight.enabled", false) !== true) return false;
  if (segment.visible === false) return false;
  if (!definition.visibilityPath) return true;
  return getAtPath(form, definition.visibilityPath, definition.defaultVisible) !== false;
}
function navbarPropertyAvailable(form, property, productsEnabled) {
  const path = String(property?.path || "");
  if (!path.startsWith("navigation.")) return true;

  const layoutTypes = new Set(getLandingSegments(form).map((segment) => segment.type));
  const aboutEnabled = layoutTypes.has("about") && getAtPath(form, "sections.showAbout", true) !== false;
  const highlightsEnabled = layoutTypes.has("highlights") && getAtPath(form, "sections.showHighlights", true) !== false;
  const productsSectionEnabled =
    productsEnabled &&
    getAtPath(form, "sections.showProducts", true) !== false &&
    ["spotlight", "featured", "brands", "categories"].some((type) => layoutTypes.has(type));
  const contactEnabled = layoutTypes.has("contact") && getAtPath(form, "sections.showContact", true) !== false;

  if (/^navigation\.(aboutLabel|aboutUrl|showAboutLink)$/.test(path)) return aboutEnabled;
  if (/^navigation\.(highlightsLabel|highlightsUrl|showHighlightsLink)$/.test(path)) return highlightsEnabled;
  if (/^navigation\.(productsLabel|productsUrl|allProductsLabel|showProductsLink|megaMenu|showMegaMenu|loadingCategoriesText|emptyCategoriesText|categoriesErrorText)/.test(path)) {
    return productsSectionEnabled;
  }
  if (/^navigation\.(contactLabel|contactUrl|showContactLink)$/.test(path)) return contactEnabled;
  return true;
}
function levelOptionsFromRegistry(visualField, key, labels) {
  const limits = visualField?.limits?.[key] || { min: 1, max: 3, step: 1 };
  const min = Number.isFinite(limits.min) ? limits.min : 1;
  const max = Number.isFinite(limits.max) ? limits.max : 3;
  const step = Number.isFinite(limits.step) && limits.step > 0 ? limits.step : 1;
  const options = [];
  for (let value = min; value <= max; value += step) {
    options.push({ value, label: labels[value] || String(value) });
  }
  return options;
}

function normalizeVisualPatch(visualField, patch) {
  return Object.fromEntries(Object.entries(patch).map(([key, rawValue]) => {
    const limits = visualField?.limits?.[key];
    if (!limits) return [key, rawValue];
    const min = Number.isFinite(limits.min) ? limits.min : rawValue;
    const max = Number.isFinite(limits.max) ? limits.max : rawValue;
    const value = Math.max(min, Math.min(max, Number(rawValue)));
    return [key, value];
  }));
}

function clampColorChannel(value) {
  return Math.max(0, Math.min(255, Math.round(Number(value) || 0)));
}

function clampOpacity(value) {
  return Math.max(0, Math.min(1, Number(value) || 0));
}

function colorParts(value) {
  const candidate = normalizeEditableColor(value);
  if (!candidate) return null;
  if (candidate === "transparent") return { r: 0, g: 0, b: 0, a: 0 };

  const hex = candidate.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    const raw = hex[1];
    if (raw.length === 3 || raw.length === 4) {
      const [r, g, b, a = "f"] = raw.split("");
      return {
        r: Number.parseInt(`${r}${r}`, 16),
        g: Number.parseInt(`${g}${g}`, 16),
        b: Number.parseInt(`${b}${b}`, 16),
        a: Number.parseInt(`${a}${a}`, 16) / 255,
      };
    }
    if (raw.length === 6 || raw.length === 8) {
      return {
        r: Number.parseInt(raw.slice(0, 2), 16),
        g: Number.parseInt(raw.slice(2, 4), 16),
        b: Number.parseInt(raw.slice(4, 6), 16),
        a: raw.length === 8 ? Number.parseInt(raw.slice(6, 8), 16) / 255 : 1,
      };
    }
  }

  const rgb = candidate.match(/^rgba?\(\s*([0-9.]+)(%)?\s*,\s*([0-9.]+)(%)?\s*,\s*([0-9.]+)(%)?(?:\s*,\s*([0-9.]+)(%)?)?\s*\)$/i);
  if (rgb) {
    const toChannel = (raw, percent) => percent ? clampColorChannel(Number(raw) * 2.55) : clampColorChannel(raw);
    const rawAlpha = rgb[7];
    const alpha = rawAlpha === undefined
      ? 1
      : rgb[8]
        ? clampOpacity(Number(rawAlpha) / 100)
        : clampOpacity(rawAlpha);
    return {
      r: toChannel(rgb[1], rgb[2]),
      g: toChannel(rgb[3], rgb[4]),
      b: toChannel(rgb[5], rgb[6]),
      a: alpha,
    };
  }

  if (typeof document !== "undefined" && isSupportedColor(candidate)) {
    try {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (context) {
        context.fillStyle = "#010203";
        context.fillStyle = candidate;
        const normalized = context.fillStyle;
        if (/^#[0-9a-f]{6}$/i.test(normalized)) return colorParts(normalized);
        const normalizedRgb = String(normalized || "").match(/^rgba?\(/i);
        if (normalizedRgb) return colorParts(normalized);
      }
    } catch {
      // Si el navegador no normaliza el color, se conserva el fallback.
    }
  }
  return null;
}

function colorPartsToHex(parts) {
  if (!parts) return "#000000";
  return `#${[parts.r, parts.g, parts.b]
    .map((channel) => clampColorChannel(channel).toString(16).padStart(2, "0"))
    .join("")}`;
}

function toColorInput(value) {
  return colorPartsToHex(colorParts(value));
}

function colorOpacityPercent(value, fallback = "#000000") {
  const parts = colorParts(value) || colorParts(fallback);
  return Math.round(clampOpacity(parts?.a ?? 1) * 100);
}

function formatColorAlpha(alpha) {
  return Number(clampOpacity(alpha).toFixed(3));
}

function colorWithOpacityPercent(value, percent, fallback = "#000000") {
  const parts = colorParts(value) || colorParts(fallback) || { r: 0, g: 0, b: 0, a: 1 };
  const alpha = clampOpacity(Number(percent) / 100);
  if (alpha >= 0.999) return colorPartsToHex(parts);
  return `rgba(${parts.r}, ${parts.g}, ${parts.b}, ${formatColorAlpha(alpha)})`;
}

function colorWithBaseHex(value, hex, fallback = "#000000") {
  const base = colorParts(hex) || colorParts(fallback) || { r: 0, g: 0, b: 0, a: 1 };
  const current = colorParts(value);
  const alpha = current?.a ?? 1;
  if (alpha >= 0.999) return colorPartsToHex(base);
  return `rgba(${base.r}, ${base.g}, ${base.b}, ${formatColorAlpha(alpha)})`;
}
function directImagePositionField(properties, imageField) {
  const candidates = [];
  if (imageField.positionPath) {
    candidates.push(imageField.positionPath);
  }
  if (imageField.path.endsWith("imageUrl")) {
    candidates.push(imageField.path.replace(/imageUrl$/, "imagePosition"));
  }
  if (imageField.path.endsWith("backgroundImageUrl")) {
    candidates.push(imageField.path.replace(/backgroundImageUrl$/, "backgroundImagePosition"));
  }
  return properties.find((property) =>
    property.type === SEGMENT_FIELD_TYPES.SELECT && candidates.includes(property.path)
  );
}

function imageAssetKey(path) {
  return String(path || "image")
    .replace(/\.\d+\./g, "-")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
}

function imageFieldsFromRegistry(definition, form) {
  const properties = definition?.properties || [];
  const directFields = properties
    .filter((property) => property.editable !== false && property.type === SEGMENT_FIELD_TYPES.IMAGE)
    .map((property) => {
      const positionField = directImagePositionField(properties, property);
      return {
        ...property,
        key: imageAssetKey(property.path),
        positionPath: positionField?.path,
        positionLabel: positionField?.label,
        positionOptions: positionField?.options || [],
        positionDefaultValue: positionField?.defaultValue,
      };
    });

  const collectionFields = properties.flatMap((property) => {
    if (property.editable === false || property.type !== SEGMENT_FIELD_TYPES.COLLECTION) return [];
    const items = getAtPath(form, property.path, []);
    if (!Array.isArray(items) || !items.length) return [];
    const itemFields = Array.isArray(property.item?.fields) ? property.item.fields : [];
    const images = itemFields.filter((itemField) => itemField.type === SEGMENT_FIELD_TYPES.IMAGE);
    if (!images.length) return [];
    const positionField = itemFields.find((itemField) =>
      itemField.type === SEGMENT_FIELD_TYPES.SELECT && /position/i.test(itemField.key || "")
    );
    const registeredMax = Number.isFinite(property.limits?.maxItems)
      ? property.limits.maxItems
      : items.length;
    const visibleItems = items.slice(0, registeredMax);

    return visibleItems.flatMap((item, index) => images.map((imageField) => {
      const path = `${property.path}.${index}.${imageField.key}`;
      return {
        ...imageField,
        path,
        label: item?.name || item?.title || item?.label || `${property.label || "Imagen"} ${index + 1}`,
        key: imageAssetKey(path),
        positionPath: positionField ? `${property.path}.${index}.${positionField.key}` : undefined,
        positionLabel: positionField?.label || "Posición de la imagen",
        positionOptions: positionField?.options || [],
        collectionPath: property.path,
        collectionIndex: index,
        collectionLength: visibleItems.length,
        collectionLabel: property.label || "Elementos",
      };
    }));
  });

  const collectionRootsWithImages = new Set(
    collectionFields
      .filter((field) => Boolean(getAtPath(form, field.path, "")))
      .map((field) => String(field.collectionPath || "").split(".")[0])
  );
  const filteredDirectFields = directFields.filter((field) => {
    const root = String(field.path || "").split(".")[0];
    return !collectionRootsWithImages.has(root);
  });

  return [...filteredDirectFields, ...collectionFields];
}

function styleFieldsFromRegistry(definition, imageFields) {
  const imagePositionPaths = new Set(imageFields.map((field) => field.positionPath).filter(Boolean));
  return (definition?.properties || []).filter((property) =>
    property.editable !== false &&
    property.type === SEGMENT_FIELD_TYPES.SELECT &&
    !imagePositionPaths.has(property.path) &&
    /(\.style|\.layout)$/.test(property.path)
  );
}

export {
  TAB_INDEX,
  INDEX_TAB,
  CONTENT_SECTION_BY_TOOL,
  DESIGN_SECTION_BY_TOOL,
  FONT_PRESETS,
  COLOR_DEFAULTS,
  PALETTE_LABELS,
  PALETTE_THEME_PATHS,
  THEME_COLOR_TOKEN_PATHS,
  SECTION_COLOR_TOKENS,
  normalizeEditableColor,
  isTransparentColor,
  globalSectionPalette,
  paletteFromForm,
  siteColorChanges,
  paletteChanges,
  hexRgb,
  relativeLuminance,
  contrastRatio,
  palettePermutations,
  randomizedReadablePalette,
  EMPTY_SEGMENT_PROPERTIES,
  getAtPath,
  CUSTOM_SECTION_LAYOUT_OPTIONS,
  CUSTOM_SECTION_IMAGE_POSITION_OPTIONS,
  editableLayoutSegment,
  customSectionDefinition,
  withSectionBackgroundProperties,
  CONTENT_FIELD_TYPES,
  BEHAVIOR_FIELD_TYPES,
  DIRECT_CONTENT_FIELD_TYPES,
  COLLECTION_CONTENT_FIELD_TYPES,
  HIGHLIGHT_ICON_COMPONENTS,
  COLLECTION_ITEM_LABELS,
  collectionTextFields,
  NON_VISUAL_TEXT_PATH,
  NON_VISUAL_TEXT_EXACT_PATHS,
  elementTextColorEligible,
  elementTextFallback,
  registeredFieldValue,
  inheritedColorValue,
  sectionColorSeed,
  isSupportedColor,
  persistedCssColorError,
  registryFieldError,
  isSectionAnchorTarget,
  manualDestinationLinkError,
  segmentAvailableAsDestination,
  navbarPropertyAvailable,
  levelOptionsFromRegistry,
  normalizeVisualPatch,
  clampColorChannel,
  clampOpacity,
  colorParts,
  colorPartsToHex,
  toColorInput,
  colorOpacityPercent,
  formatColorAlpha,
  colorWithOpacityPercent,
  colorWithBaseHex,
  directImagePositionField,
  imageAssetKey,
  imageFieldsFromRegistry,
  styleFieldsFromRegistry
};
